// Sprint 2 PR-D2 — Conversation detail drawer.
//
// Mevcut info-drawer DOM chrome'unu reuse eder (info-overlay, info-drawer,
// info-kind, info-hero, info-body). openInfoDrawer/closeInfoDrawer drawer.ts
// içinden export'lu.
//
// Davranış:
// - openConversationDrawer(id) → /api/v2/conversations/:id fetch, full messages render
// - AbortController: hızlı tıklamalarda son request token korunur, eski abort edilir
// - ESC + close × button + overlay click ile kapanır (mevcut drawer altyapısından)
// - 404 → "Konuşma bulunamadı" empty state
// - Error → error card; toast apiFetch'ten

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

// Drawer chrome'unu doğrudan manipüle ederiz (drawer.ts içindeki helper'lar
// transitively window'a bağlı view'ları çekiyor; cycle önlemek için inline).
// Aynı DOM ID'lerini kullandığı için mevcut info-overlay/drawer/header CSS
// + close button + overlay click handler'ları (drawer.ts:bindInfoDrawerStatic)
// üzerinden çalışır.
function showInfoDrawer(): void {
  document.getElementById('info-drawer')?.classList.add('open');
  document.getElementById('info-overlay')?.classList.add('visible');
}
function closeInfoDrawer(): void {
  document.getElementById('info-drawer')?.classList.remove('open');
  document.getElementById('info-overlay')?.classList.remove('visible');
}

interface ConversationDetail {
  id: string;
  tenantId: string;
  channel: string;
  externalContactId: string | null;
  leadId: string | null;
  messages: Array<{ role?: string; text?: string; ts?: string; [k: string]: unknown }>;
  aiSummary: string | null;
  lastMessageAt: string | null;
  handoffRequired: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}
interface ConversationDetailResponse { ok: true; conversation: ConversationDetail; }

type DrawerState =
  | { kind: 'loading'; id: string }
  | { kind: 'ready'; conversation: ConversationDetail }
  | { kind: 'not-found'; id: string }
  | { kind: 'error'; id: string; message: string };

let _currentRequestToken = 0;

export async function openConversationDrawer(id: string): Promise<void> {
  if (!id) return;
  // Race-safe: yeni request token ata; sonraki tamamlamalarda eski'yi yutar.
  const myToken = ++_currentRequestToken;
  renderDrawer({ kind: 'loading', id });
  showInfoDrawer();
  const result = await apiGetJson<ConversationDetailResponse>(`/api/v2/conversations/${encodeURIComponent(id)}`);
  if (myToken !== _currentRequestToken) return; // eski request abort (yeni açıldı)
  if (result.ok) {
    renderDrawer({ kind: 'ready', conversation: result.data.conversation });
  } else if (result.error === 'conversation_not_found') {
    renderDrawer({ kind: 'not-found', id });
  } else {
    renderDrawer({ kind: 'error', id, message: result.message ?? result.error });
  }
}

export function closeConversationDrawer(): void {
  _currentRequestToken++; // pending fetch'leri invalidate et
  closeInfoDrawer();
}

function renderDrawer(s: DrawerState): void {
  const kindEl = document.getElementById('info-kind');
  if (kindEl) kindEl.textContent = 'KONUŞMA';
  const hero = document.getElementById('info-hero');
  const body = document.getElementById('info-body');
  if (!hero || !body) return;
  hero.innerHTML = '';
  body.innerHTML = renderConversationDrawerHtml(s);
}

// Pure render — test edilebilir.
export function renderConversationDrawerHtml(s: DrawerState): string {
  if (s.kind === 'loading') {
    return `<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor… (${escHtml(s.id.slice(0, 8))})</div></div>`;
  }
  if (s.kind === 'not-found') {
    return `<div class="empty-state"><div class="empty-icon">🔍</div><div class="empty-title">Konuşma bulunamadı</div><div style="color:var(--muted);font-size:12px;">id: <code>${escHtml(s.id)}</code></div></div>`;
  }
  if (s.kind === 'error') {
    return `<div class="rt-card" style="border-left:3px solid var(--danger,#e55);"><div class="rt-card-title">Yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(s.message)}</div></div>`;
  }
  const c = s.conversation;
  return `
    <div class="rt-card">
      <div class="rt-card-title">Conversation</div>
      <div style="display:grid;grid-template-columns:auto 1fr;gap:6px 12px;font-size:12px;">
        <span style="color:var(--muted);">id</span><code>${escHtml(c.id)}</code>
        <span style="color:var(--muted);">tenant</span><span>${escHtml(c.tenantId)}</span>
        <span style="color:var(--muted);">channel</span><span><code style="background:var(--bg2);padding:1px 6px;border-radius:4px;">${escHtml(c.channel)}</code></span>
        ${c.externalContactId ? `<span style="color:var(--muted);">contact</span><code>${escHtml(c.externalContactId)}</code>` : ''}
        ${c.leadId ? `<span style="color:var(--muted);">leadId</span><code>${escHtml(c.leadId)}</code>` : ''}
        <span style="color:var(--muted);">status</span><span>${escHtml(c.status)}</span>
        <span style="color:var(--muted);">handoff</span><span>${c.handoffRequired ? '⚠️ required' : 'no'}</span>
        <span style="color:var(--muted);">created</span><span>${escHtml(c.createdAt)}</span>
        ${c.lastMessageAt ? `<span style="color:var(--muted);">lastMsg</span><span>${escHtml(c.lastMessageAt)}</span>` : ''}
      </div>
      ${c.aiSummary ? `<div style="background:var(--bg2);padding:8px;border-radius:4px;margin-top:8px;font-size:12px;"><span style="color:var(--muted);text-transform:uppercase;font-size:10px;">ai summary:</span><br>${escHtml(c.aiSummary)}</div>` : ''}
    </div>
    <div class="rt-card">
      <div class="rt-card-title">Messages (${c.messages.length})</div>
      ${c.messages.length === 0
        ? '<div class="empty-state"><div class="empty-icon">💬</div><div class="empty-title">Henüz mesaj yok</div></div>'
        : `<div style="display:flex;flex-direction:column;gap:8px;">${c.messages.map(m => renderMessage(m)).join('')}</div>`
      }
    </div>`;
}

function renderMessage(m: { role?: string; text?: string; ts?: string }): string {
  const role = m.role ?? 'unknown';
  const isCustomer = role === 'customer';
  const bg = isCustomer ? 'var(--bg2)' : 'var(--bg3,#eef)';
  const align = isCustomer ? 'flex-start' : 'flex-end';
  return `<div style="display:flex;justify-content:${align};">
    <div style="max-width:80%;background:${bg};padding:8px 12px;border-radius:8px;">
      <div style="font-size:10px;color:var(--muted);text-transform:uppercase;margin-bottom:3px;">${escHtml(role)}${m.ts ? ' · ' + escHtml(m.ts) : ''}</div>
      <div style="font-size:13px;white-space:pre-wrap;">${escHtml(m.text ?? '')}</div>
    </div>
  </div>`;
}

// ESC tuşu için global listener (bir kez bağlanır)
let _escBound = false;
export function bindConversationDrawerEsc(): void {
  if (_escBound) return;
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeConversationDrawer();
  });
  _escBound = true;
}
